import { useEffect, useState } from "react"
import { usePage } from "@inertiajs/react"
import { CopyToClipboard } from "react-copy-to-clipboard-ts"
import { CheckIcon, LinkIcon } from "@heroicons/react/24/outline"
import { FaSquareXTwitter } from "react-icons/fa6"
import { FaFacebookSquare } from "react-icons/fa"
import { Modals, useModals } from "@/hooks/useModals"
import { FeaturedPromotion } from "@/types/serializers"
import Button from "@/components/Button"
import Modal from "@/components/Modal"
import clsx from "clsx"

const RadioButton = ({
  checked,
  onChange,
  name,
  id,
  label,
}: {
  checked: boolean
  onChange: () => void
  name: string
  id: string
  label: string
}) => (
  <div className="flex items-center">
    <input
      id={id}
      checked={checked}
      onChange={onChange}
      name={name}
      type="radio"
      className="text-chessdrop-600 focus:ring-chessdrop-600 h-4 w-4 border-gray-300"
    />
    <label
      htmlFor={id}
      className="ml-3 block text-sm/6 font-medium text-gray-700"
    >
      {label}
    </label>
  </div>
)

const ShareModal = ({
  url,
  title,
  type,
}: {
  url: string
  title: string
  type: "game" | "player" | "event" | "puzzle"
}) => {
  const { featured_promotions } = usePage().props
  const { activeModal, closeModal } = useModals()
  const isOpen = activeModal === Modals.Share

  const showPromotions = type === "game" && featured_promotions.length > 0
  const [selectedPromotion, setSelectedPromotion] =
    useState<FeaturedPromotion | null>(null)

  const [copied, setCopied] = useState(false)

  useEffect(() => {
    const timeout = setTimeout(() => setCopied(false), 1000)
    return () => clearTimeout(timeout)
  }, [copied])

  const twitterShareText =
    type === "game"
      ? `Check out this chess game on @ChessDrop\n\n${url}${
          selectedPromotion ? `\n\n${selectedPromotion.hashtag}` : ""
        }`
      : url

  return (
    <Modal
      show={isOpen}
      onClose={closeModal}
      title={title}
      maxWidthClassName="max-w-md"
    >
      <div className="mt-4 space-y-2">
        <div>
          <div className="rounded-xl border border-gray-200 bg-gray-100 px-4 py-3.5 text-center text-sm font-medium text-gray-800 shadow-inner select-all">
            {url}
          </div>

          <CopyToClipboard text={url} onCopy={() => setCopied(true)}>
            <Button fullWidth full style="secondary" size="lg" className="mt-2">
              {copied ? (
                <>
                  <CheckIcon className="mr-1 h-5 w-5 shrink-0" />
                  <span>Link copied!</span>
                </>
              ) : (
                <>
                  <LinkIcon className="mr-1 h-5 w-5 shrink-0" />
                  <span>Copy link</span>
                </>
              )}
            </Button>
          </CopyToClipboard>
        </div>

        <Button
          fullWidth
          style="secondary"
          size="lg"
          href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            url
          )}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <FaFacebookSquare className="mr-1 h-6 w-6 shrink-0 text-[#4267B2]" />
          Share on Facebook
        </Button>

        <div
          className={clsx({
            "rounded-2xl border border-gray-200 bg-gray-100 p-2 shadow-inner":
              showPromotions,
          })}
        >
          <Button
            fullWidth
            style="secondary"
            size="lg"
            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
              twitterShareText
            )}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaSquareXTwitter className="mr-1 h-6 w-6 shrink-0" />
            Share on X
          </Button>

          {showPromotions && (
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-700">
                Select a promotion to include in your tweet:
              </p>
              <div className="mt-2 space-y-2">
                {featured_promotions.map((promotion) => (
                  <RadioButton
                    key={promotion.id}
                    checked={selectedPromotion?.id === promotion.id}
                    onChange={() => setSelectedPromotion(promotion)}
                    name="promotion"
                    id={promotion.slug}
                    label={promotion.title}
                  />
                ))}
                <RadioButton
                  checked={!selectedPromotion}
                  onChange={() => setSelectedPromotion(null)}
                  name="promotion"
                  id="none"
                  label="Don’t include a promotion"
                />
              </div>
              {selectedPromotion && (
                <p className="mt-2 text-sm font-medium text-pretty text-gray-700">
                  {selectedPromotion.auto_discovery_enabled ? (
                    <>
                      Matching public posts are detected automatically. Keep
                      @ChessDrop, this hashtag, and the game link in your post
                      so it can be verified reliably.
                    </>
                  ) : (
                    <>
                      Remember to post a link to your tweet on the{" "}
                      <a
                        href={`/p/${selectedPromotion.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-gray-900"
                      >
                        promotion page
                      </a>
                      .
                    </>
                  )}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default ShareModal
